import axios from 'axios';

// Auth is cookie-based (httpOnly accessToken / refreshToken). The browser owns
// the tokens, so every request must be sent with credentials and no
// Authorization header is ever built client-side.
const api = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}`,
  withCredentials: true,
});

// Demo mode: serve every route from the in-memory dataset (no backend needed).
// Enabled with REACT_APP_DEMO=1 — used for sales demos and marketing screenshots.
if (process.env.REACT_APP_DEMO === "1") {
  const demoAdapter = require("./demo/adapter").default;
  api.defaults.adapter = demoAdapter;
}

// Endpoints whose own 401 means "bad credentials / session over", not
// "access token expired" — never try to refresh for these.
const NO_REFRESH_PATHS = ['/auth/lab/login', '/auth/refresh-token', '/auth/logout', '/auth/switch-lab'];

// Fired when the refresh cookie is also gone; AuthProvider listens and clears
// the user so RequireAuth routes to /login without a full page reload.
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

// Response Interceptor: refresh once on 401, then retry the original request
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url ?? '';
    const skip = NO_REFRESH_PATHS.some((p) => url.includes(p));

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !skip) {
      originalRequest._retry = true;
      try {
        // Refresh cookie is sent automatically; server sets a new accessToken cookie
        await api.post('/auth/refresh-token');
        return api(originalRequest);
      } catch (refreshError) {
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
